import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import { userApi } from '@/api/user'
import type { User } from '@/types/User'
import type { LoginForm } from '@/types/LoginForm'
import type { RegisterForm } from '@/types/RegisterForm'
import { message } from 'ant-design-vue'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>(localStorage.getItem('token') || '')
  const refreshTokenValue = ref<string>(localStorage.getItem('refreshToken') || '')
  const user = ref<User | null>(null)
  const isRefreshing = ref(false)
  const refreshPromise = ref<Promise<any> | null>(null)

  const isAuthenticated = computed(() => !!token.value)

  // 登录接口仅返回令牌，登录成功后再通过 /me 拉取当前用户信息（含权限）。
  const login = async (loginForm: LoginForm) => {
    try {
      const response = await authApi.login(loginForm)

      token.value = response.data.accessToken
      refreshTokenValue.value = response.data.refreshToken

      localStorage.setItem('token', token.value)
      localStorage.setItem('refreshToken', refreshTokenValue.value)

      // 令牌就绪后立即获取用户信息，保证后续路由守卫能拿到权限
      await fetchCurrentUser()

      message.success('Login successful')
      return response
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Login failed')
      throw error
    }
  }

  // 通过 /admin/api/v1/users/me 获取当前登录用户信息（含 roles + permissions）。
  // 不写入 localStorage：用户态以服务端为准，刷新页面时按需重新拉取，保证权限实时。
  const fetchCurrentUser = async () => {
    const response = await userApi.getCurrentUser()
    user.value = response.data
    return user.value
  }

  const register = async (registerForm: RegisterForm) => {
    try {
      const response = await authApi.register(registerForm)
      message.success('Registration successful! Please login.')
      return response
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Registration failed')
      throw error
    }
  }

  const refreshToken = async () => {
    try {
      // 如果已经在刷新，返回现有的Promise
      if (refreshPromise.value) {
        return refreshPromise.value
      }

      // 如果没有refresh token，直接返回错误
      if (!refreshTokenValue.value) {
        throw new Error('No refresh token available')
      }

      isRefreshing.value = true
      
      // 创建新的刷新Promise
      refreshPromise.value = authApi.refreshToken(refreshTokenValue.value)
        .then(response => {
          if (!response.data?.accessToken || !response.data?.refreshToken) {
            throw new Error('Invalid refresh token response')
          }

          token.value = response.data.accessToken
          refreshTokenValue.value = response.data.refreshToken
          
          localStorage.setItem('token', token.value)
          localStorage.setItem('refreshToken', refreshTokenValue.value)
          
          return response
        })
        .catch(error => {
          console.error('Token refresh failed:', error)
          logout()
          throw error
        })
        .finally(() => {
          isRefreshing.value = false
          refreshPromise.value = null
        })

      return refreshPromise.value
    } catch (error: any) {
      logout()
      throw new Error(error?.response?.data?.message || 'Token refresh failed')
    }
  }

  const logout = () => {
    token.value = ''
    refreshTokenValue.value = ''
    user.value = null
    refreshPromise.value = null
    isRefreshing.value = false

    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')

    message.success('Logged out successfully')
  }

  return {
    token,
    refreshTokenValue,
    user,
    isAuthenticated,
    isRefreshing,
    login,
    register,
    refreshToken,
    fetchCurrentUser,
    logout,
  }
})